import { type CategoryOption } from "portal/utils/categoryCollection/category";
import { Entity } from "portal/components/filters/filter.types";
import { PlantProfileCategoryAutocomplete } from "portal/components/categoryCollectionProfile/PlantProfileCategoryAutocomplete";
import { usePlantProfileCategoryOptions } from "portal/components/categoryCollectionProfile/form/usePlantProfileCategoryOptions";
import { useTranslation } from "react-i18next";
import React, { FC, useCallback, useMemo } from "react";

interface PlantProfileCategoryFilterProps {
  scopeCustomerUuid?: string;
  value: Entity | undefined;
  onChange: (value: Entity | undefined) => void;
  disabled?: boolean;
}

export const PlantProfileCategoryFilter: FC<
  PlantProfileCategoryFilterProps
> = ({ scopeCustomerUuid, value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const { categoryOptions, isLoading, hasMore, loadMore, onSearchInputChange } =
    usePlantProfileCategoryOptions(scopeCustomerUuid);

  const selectedOption = useMemo((): CategoryOption | undefined => {
    if (!value) {
      return;
    }
    return (
      categoryOptions.find((option) => option.id === value.id) ?? {
        id: value.id,
        name: value.name,
        displayName: value.name,
        chipIds: [],
      }
    );
  }, [categoryOptions, value]);

  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      loadMore();
    }
  }, [hasMore, loadMore]);

  return (
    <PlantProfileCategoryAutocomplete
      size="small"
      disabled={disabled || !scopeCustomerUuid}
      loading={isLoading}
      label={t("components.categoryCollectionProfile.filters.sourceCategory")}
      categoryOptions={categoryOptions}
      textFieldClassName="min-w-32 md:min-w-56"
      // MUI Autocomplete uses null for an empty single-select value.
      // eslint-disable-next-line unicorn/no-null
      value={selectedOption ?? null}
      onSearchInputChange={onSearchInputChange}
      onLoadMore={handleLoadMore}
      onChange={(option) => {
        if (!option || typeof option === "string" || Array.isArray(option)) {
          onChange(undefined);
          return;
        }
        onChange({
          id: option.id,
          name: option.displayName,
        });
      }}
    />
  );
};
